import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Icon from '@amzn/meridian/icon';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { ProcessPathState, StaffingMode } from 'src/pages/settings/tabs/siteLayout/types';
import { useAppDispatch } from 'src/store';
import {
  removeProcessPath,
  setStaffingMode,
  addLine,
  removeLine,
  updateLineName,
} from 'src/store/slices/siteLayoutSlice';
import StaffingModeToggle from './StaffingModeToggle';
import LineCardList from './LineCardList';
import WorkstationChipList from './WorkstationChipList';

export interface ProcessPathCardProps {
  processPath: ProcessPathState;
  processPathIndex: number;
}

const ProcessPathCard: React.FC<ProcessPathCardProps> = ({
  processPath,
  processPathIndex,
}) => {
  const dispatch = useAppDispatch();
  const [pendingMode, setPendingMode] = useState<StaffingMode | null>(null);

  const handleDelete = () => {
    dispatch(removeProcessPath(processPathIndex));
  };

  const handleStaffingModeChange = (mode: StaffingMode) => {
    if (mode === processPath.staffingMode) return;
    // Check if switching will clear existing data
    const hasData = processPath.staffingMode === 'PROCESS_LINES'
      ? processPath.lines.length > 0
      : processPath.individualWorkstations.length > 0;

    if (hasData) {
      setPendingMode(mode);
    } else {
      dispatch(setStaffingMode({ processPathIndex, mode }));
    }
  };

  const handleConfirmModeChange = () => {
    if (pendingMode) {
      dispatch(setStaffingMode({ processPathIndex, mode: pendingMode }));
      setPendingMode(null);
    }
  };

  const handleCancelModeChange = () => {
    setPendingMode(null);
  };

  const handleAddLine = () => {
    dispatch(addLine({ processPathIndex }));
  };

  const handleDeleteLine = (lineIndex: number) => {
    dispatch(removeLine({ processPathIndex, lineIndex }));
  };

  const handleLineNameChange = (lineIndex: number, name: string) => {
    dispatch(updateLineName({ processPathIndex, lineIndex, name }));
  };

  return (
    <Box type="outline" spacingInset="400" data-testid={`process-path-card-${processPathIndex}`}>
      {/* Header row */}
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Row spacing="300" alignmentVertical="center">
          <Text type="b400" tag="span">
            {processPath.functionName}
          </Text>
          <Text type="b200" color="secondary" tag="span">
            {processPath.functionId}
          </Text>
        </Row>
        <Button
          type="icon"
          size="small"
          onClick={handleDelete}
          data-testid={`delete-process-path-${processPathIndex}`}
          label="Delete process path"
        >
          <Icon tokens={trashTokens} />
        </Button>
      </Row>

      {/* Staffing mode toggle */}
      <Box spacingInset="300 none">
        <StaffingModeToggle
          mode={processPath.staffingMode}
          onChange={handleStaffingModeChange}
        />
      </Box>

      {/* Content area */}
      <Box spacingInset="300 none">
        {processPath.staffingMode === 'PROCESS_LINES' && (
          <LineCardList
            lines={processPath.lines}
            processPathIndex={processPathIndex}
            onAddLine={handleAddLine}
            onDeleteLine={handleDeleteLine}
            onLineNameChange={handleLineNameChange}
          />
        )}

        {processPath.staffingMode === 'DEPARTMENT' && (
          <Row widths="grid-3">
            <WorkstationChipList
              workstations={processPath.individualWorkstations}
              processPathIndex={processPathIndex}
            />
          </Row>
        )}
      </Box>

      {/* Confirmation modal for staffing mode switch */}
      <Modal
        open={pendingMode !== null}
        title="Confirm Mode Change"
        onClose={handleCancelModeChange}
      >
        <Text>
          Switching staffing mode will clear all existing {processPath.staffingMode === 'PROCESS_LINES' ? 'lines and workstations' : 'individual workstations'}. This action cannot be undone.
        </Text>
        <ModalFooter>
          <Row spacing="300" alignmentHorizontal="end">
            <Button type="secondary" onClick={handleCancelModeChange}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleConfirmModeChange}>
              Confirm
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </Box>
  );
};

export default ProcessPathCard;
